import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { IoMdArrowDropdown } from 'react-icons/io';
import { useOutsideClick } from '../../../base/context/OutsideClick';
import './style.css';

export default function MultiSelectDropdown({ options, placeholder, setValues, className }) {
  const [dropdownOpt, setDropdownOpt] = useState([]);
  const [optionVisibility, setOptionVisibility] = useState(false);
  const [addOthers, setAddOthers] = useState(false);
  const [otherOption, setOtherOption] = useState('');
  const outsideClick = useOutsideClick();
  const ref = useRef();

  // Initialize options when they change
  useEffect(() => {
    if (options && options.length > 0) {
      const initializedOptions = options.map(item => ({
        ...item,
        selected: item.selected || false,
      }));
      setDropdownOpt(initializedOptions);
    }
  }, [options]);

  const handleValues = useCallback(
    option => {
      setDropdownOpt(prev => {
        const updated = prev.map(item => (item.name === option.name ? { ...item, selected: !item.selected } : item));
        setValues(updated);
        return updated;
      });
    },
    [setValues]
  );

  const handleDropdown = useCallback(() => {
    setOptionVisibility(true);
  }, []);

  const handleClose = useCallback(() => {
    setOptionVisibility(false);
  }, []);

  useEffect(() => {
    outsideClick.getComponent(ref, handleClose);
  }, [outsideClick, handleClose]);

  const handleOther = useCallback(() => {
    setAddOthers(prev => !prev);
  }, []);

  const handleOtherOption = useCallback(e => {
    setOtherOption(e.target.value);
  }, []);

  const addOtherOption = useCallback(
    e => {
      e.preventDefault();
      if (!otherOption.trim()) return;

      const newOption = {
        name: otherOption.trim(),
        selected: true,
      };

      setDropdownOpt(prev => {
        const updated = [...prev, newOption];
        setValues(updated);
        return updated;
      });
      setOtherOption('');
      setAddOthers(false);
    },
    [otherOption, setValues]
  );

  useEffect(() => {
    if (addOthers) {
      const input = document.getElementById('otherInput');
      if (input) input.focus();
    }
  }, [addOthers]);

  const selectedItems = useMemo(() => dropdownOpt.filter(item => item.selected), [dropdownOpt]);

  const hasSelectedItems = selectedItems.length > 0;

  const displayContent = useMemo(() => {
    if (!hasSelectedItems) return placeholder;

    if (selectedItems.length <= 3) {
      return selectedItems.map((item, index) => (
        <span key={index} className="text-nowrap inline bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm">
          {item.name}
          {index < selectedItems.length - 1 ? ', ' : ''}
        </span>
      ));
    } else {
      return (
        <>
          {selectedItems.slice(0, 2).map((item, index) => (
            <span key={index} className="text-nowrap inline bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm">
              {item.name},
            </span>
          ))}
          <span className="text-nowrap inline bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm">
            +{selectedItems.length - 2} more
          </span>
        </>
      );
    }
  }, [hasSelectedItems, selectedItems, placeholder]);

  return (
    <div className="multi_select_dropdown w-full relative my-1">
      <div className={`${className || 'border h-12 px-3 py-2 w-full rounded-full flex'}`} onClick={handleDropdown}>
        <div className="flex justify-between items-center w-full">
          <div className="w-11/12 flex overflow-x-auto overflow-y-hidden gap-2">{displayContent}</div>
          <IoMdArrowDropdown size={20} className={optionVisibility ? 'origin-center rotate-180' : ''} />
        </div>
      </div>

      {optionVisibility && (
        <div ref={ref} className="absolute w-full max-h-52 bg-white border p-2 rounded z-50 top-12 overflow-y-auto">
          {dropdownOpt.map((item, index) => (
            <div
              key={`${item.name}-${index}`}
              className="flex gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
              onClick={() => handleValues(item)}
            >
              <input type="checkbox" checked={item?.selected || false} readOnly onClick={e => e.stopPropagation()} />
              <span>{item?.name}</span>
            </div>
          ))}
          <div className="flex gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded" onClick={handleOther}>
            <input type="checkbox" checked={addOthers} readOnly onClick={e => e.stopPropagation()} />
            <span>Other</span>
          </div>

          {addOthers && (
            <div className="mt-2">
              <input
                className="w-full h-10 border px-2 rounded"
                onChange={handleOtherOption}
                value={otherOption}
                id="otherInput"
                placeholder="Enter custom option"
              />
              <button
                className="bg-slate-500 py-1 px-4 my-2 text-white rounded hover:bg-slate-600"
                onClick={addOtherOption}
                disabled={!otherOption.trim()}
              >
                Add
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
